import type { DashboardUser } from '../auth/auth-adapter'
import { z } from 'zod'
import { getEnvironment } from '../../lib/environment'
import { createApiClient } from '../../services/api/client'
import {
  bookAppointmentRequestSchema,
  bookAppointmentResponseSchema,
  bookingOptionsRequestSchema,
  bookingOptionsResponseSchema,
  type BookAppointmentRequest,
  type BookAppointmentResponse,
  type BookingOptionsRequest,
  type BookingOptionsResponse,
} from './appointment-booking-contracts'

export type AppointmentBookingRepository = {
  options: (
    tenantId: string,
    request: BookingOptionsRequest
  ) => Promise<BookingOptionsResponse>
  book: (
    tenantId: string,
    request: BookAppointmentRequest
  ) => Promise<BookAppointmentResponse>
}

export function createAppointmentBookingRepository({
  getUser,
  baseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation,
}: {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}): AppointmentBookingRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })
  const errorSchema = z.object({ message: z.string().min(1) }).strict()
  const endpoint = (tenantId: string, operation: 'options' | 'book') =>
    `/dashboard/clinics/${encodeURIComponent(tenantId)}/booking/${operation}`
  return {
    options: (tenantId, value) =>
      client.request(endpoint(tenantId, 'options'), {
        method: 'POST',
        body: bookingOptionsRequestSchema.parse(value),
        responseSchema: bookingOptionsResponseSchema,
        errorSchema,
      }),
    book: (tenantId, value) =>
      client.request(endpoint(tenantId, 'book'), {
        method: 'POST',
        body: bookAppointmentRequestSchema.parse(value),
        responseSchema: bookAppointmentResponseSchema,
        errorSchema,
      }),
  }
}
